// Web Push: показ уведомления и разбор клика. SPEC pwa-shell §4.4 / §4.5 (инв.1 deep-link).
//
// ЧИСТЫЕ функции (тестируются в Node без браузера) — носители инв.1 «клик по пушу открывает
// КОНКРЕТНОЕ сообщение, обойти нельзя»:
//   onPush(payloadJson)            → { title, options } для showNotification (deep-link в data);
//   onNotificationClick(notif)     → целевой deep-link (из data).
// SW-точка (sw.ts) лишь вызывает их и дёргает реальные showNotification/clients — без логики.
//
// Тип входящего пуша — PushPayload из shared-contracts (§4.8); локально НЕ дублируется.
// Секреты в уведомление НЕ кладём: только поля PushPayload (title/body?/deepLink/chatId/messageId/
// unreadTotal); ничего из «сырого» JSON, кроме них, наружу не переносится (D-08 — payload читаем SW).
import { CHATS_ROOT, buildDeepLink, parseDeepLink } from './deep-link.js';
/** Свои нейтральные ассеты уведомления (инв.2: без брендинга Telegram). */
const BADGE_ASSET = '/icons/badge-96.png';
const ICON_ASSET = '/icons/icon-192.png';
/** Заголовок дженерик-уведомления, когда payload пуст/битый (§4.9). */
const GENERIC_TITLE = 'Новое сообщение';
/**
 * Структурная валидация распарсенного JSON как PushPayload (форма push-payload.json).
 * Без ajv в SW-бандле (минимизируем вес) — ровно те же required-поля/типы, что в схеме:
 * chatId/messageId/title/deepLink:string, unreadTotal:integer≥0, body?:string.
 */
function isPushPayload(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    if (typeof v['chatId'] !== 'string' || v['chatId'].length === 0)
        return false;
    if (typeof v['messageId'] !== 'string' || v['messageId'].length === 0)
        return false;
    if (typeof v['title'] !== 'string')
        return false;
    if (typeof v['deepLink'] !== 'string' || v['deepLink'].length === 0)
        return false;
    if (typeof v['unreadTotal'] !== 'number' || !Number.isInteger(v['unreadTotal']) || v['unreadTotal'] < 0) {
        return false;
    }
    if (v['body'] !== undefined && typeof v['body'] !== 'string')
        return false;
    return true;
}
/**
 * Разбирает сырой payload пуша (`event.data?.text()`/JSON-строку) в аргументы showNotification.
 * НИКОГДА не бросает (SW не должен падать, §4.9): на пустой/битый/несхемный вход — дженерик-
 * уведомление «Новое сообщение» с deep-link в корень чатов (деградация), badge не трогаем.
 *
 * Для валидного PushPayload (инв.1):
 *   - title = payload.title; body — ТОЛЬКО если payload.body есть (иначе «без превью» — нет body);
 *   - tag = chatId (схлопывание), renotify=true;
 *   - data.deepLink = payload.deepLink (носитель инв.1); если deepLink не ведёт в конкретное
 *     сообщение — пересобираем канон `/chat/<chatId>?msg=<messageId>` из chatId/messageId, чтобы
 *     клик ВСЕГДА открывал сообщение, а не список (инв.1 «обойти нельзя»);
 *   - badgeCount = unreadTotal (абсолютное, §4.6).
 */
export function onPush(payloadJson) {
    let parsed;
    if (typeof payloadJson === 'string' && payloadJson.length > 0) {
        try {
            parsed = JSON.parse(payloadJson);
        }
        catch {
            parsed = undefined;
        }
    }
    if (!isPushPayload(parsed)) {
        // Деградация (§4.9): дженерик без превью, навигация в корень чатов, без badge.
        return {
            title: GENERIC_TITLE,
            options: genericOptions(),
        };
    }
    const payload = parsed;
    const chatId = payload.chatId;
    const messageId = payload.messageId;
    // Гарантия инв.1: data.deepLink ОБЯЗАН вести в конкретное сообщение. Доверяем готовому
    // payload.deepLink, только если он ведёт в сообщение; иначе пересобираем канон сами.
    const target = parseDeepLink(payload.deepLink);
    const deepLink = target !== null && target.messageId !== undefined
        ? payload.deepLink
        : buildDeepLink(chatId, messageId);
    const options = {
        tag: chatId,
        renotify: true,
        data: { deepLink, chatId, messageId },
        badge: BADGE_ASSET,
        icon: ICON_ASSET,
        lang: 'ru',
        dir: 'ltr',
        timestamp: Date.now(),
    };
    // body — ТОЛЬКО при наличии превью (FR-NOTIF-03 «без превью» → поля нет вовсе, не пустая строка).
    if (payload.body !== undefined) {
        options.body = payload.body;
    }
    return {
        title: payload.title,
        options,
        badgeCount: payload.unreadTotal,
    };
}
function genericOptions() {
    return {
        tag: 'generic',
        renotify: true,
        // Корень чатов — единственный допустимый «без сообщения» путь; messageId отсутствует осознанно.
        data: { deepLink: CHATS_ROOT, chatId: '' },
        badge: BADGE_ASSET,
        icon: ICON_ASSET,
        lang: 'ru',
        dir: 'ltr',
        timestamp: Date.now(),
    };
}
/**
 * Извлекает целевой deep-link из нажатого уведомления (§4.5, инв.1). Чистая функция.
 * Берёт `notification.data.deepLink` (его положил onPush). Если data повреждена/отсутствует —
 * деградирует в корень чатов (§4.9), НИКОГДА не бросает (SW-обработчик не падает).
 * Возвращаемая строка — то, что SW передаст в postMessage{navigate} существующему окну либо в
 * clients.openWindow(): обе ветки ведут к этому deepLink (нет ветки «открыть список вместо сообщения»).
 */
export function onNotificationClick(notification) {
    const data = notification.data;
    if (typeof data === 'object' && data !== null) {
        const deepLink = data['deepLink'];
        if (typeof deepLink === 'string' && deepLink.length > 0) {
            return deepLink;
        }
    }
    return CHATS_ROOT;
}
//# sourceMappingURL=notification.js.map